import { type ReactNode } from "react";

import { type DealTrackingLimitedPartnerBreakdown } from "@vega-api/generated/types/dealTrackingLimitedPartnerBreakdown";
import { EMPTY_CELL_VALUE } from "@vega-core/constants/global";
import { m } from "@vega-core/paraglide/messages";
import { formatMoney, formatMoneyShort } from "@vega-core/utils/formatMoney";
import { richTextFormat } from "@vega-core/utils/i18nHelpers";

export const formatLimitedPartnerCellText = ({
  limitedPartnerName,
}: DealTrackingLimitedPartnerBreakdown): string => limitedPartnerName;

export const formatTierCellText = ({
  tier,
}: DealTrackingLimitedPartnerBreakdown): string => tier || EMPTY_CELL_VALUE;

export const formatStageCellText = ({
  stage,
}: DealTrackingLimitedPartnerBreakdown): string => {
  const trimmed = stage?.trim();

  if (!trimmed) return EMPTY_CELL_VALUE;

  return trimmed;
};

export const formatNdaCellText = ({
  numberOfNdasSigned,
  numberOfNdasIssued,
}: DealTrackingLimitedPartnerBreakdown): string => {
  if (numberOfNdasIssued === 0) {
    return EMPTY_CELL_VALUE;
  }

  return `${m.common_signed()} ${numberOfNdasSigned}/${numberOfNdasIssued}`;
};

export const formatTicketSizeCellText = ({
  ticketSize,
}: DealTrackingLimitedPartnerBreakdown): string =>
  ticketSize
    ? `${formatMoney(ticketSize.minimum)} — ${formatMoney(ticketSize.maximum)}`
    : EMPTY_CELL_VALUE;

export const formatLpTicketSizeMinCellText = ({
  ticketSize,
}: DealTrackingLimitedPartnerBreakdown): string =>
  ticketSize ? formatMoneyShort(ticketSize.minimum) : EMPTY_CELL_VALUE;

export const formatLpTicketSizeMaxCellText = ({
  ticketSize,
}: DealTrackingLimitedPartnerBreakdown): string =>
  ticketSize ? formatMoneyShort(ticketSize.maximum) : EMPTY_CELL_VALUE;

export const formatIndicativeAmountCellText = ({
  indicativeAmount,
}: DealTrackingLimitedPartnerBreakdown): string =>
  indicativeAmount ? formatMoneyShort(indicativeAmount) : EMPTY_CELL_VALUE;

export const formatGpEstimatedTicketSizeMinCellText = ({
  estimatedTicketSizeMin,
}: DealTrackingLimitedPartnerBreakdown): string =>
  estimatedTicketSizeMin
    ? formatMoneyShort(estimatedTicketSizeMin)
    : EMPTY_CELL_VALUE;

export const formatGpEstimatedTicketSizeMaxCellText = ({
  estimatedTicketSizeMax,
}: DealTrackingLimitedPartnerBreakdown): string =>
  estimatedTicketSizeMax
    ? formatMoneyShort(estimatedTicketSizeMax)
    : EMPTY_CELL_VALUE;

export const formatGpIndicativeOrderCellText = ({
  gpIndicativeOrder,
}: DealTrackingLimitedPartnerBreakdown): string =>
  gpIndicativeOrder ? formatMoneyShort(gpIndicativeOrder) : EMPTY_CELL_VALUE;

export const formatProbabilityCellText = ({
  probability,
}: DealTrackingLimitedPartnerBreakdown): string =>
  probability == null ? EMPTY_CELL_VALUE : `${probability}%`;

export const formatWeightedOrderCellText = ({
  weightedOrder,
}: DealTrackingLimitedPartnerBreakdown): string =>
  weightedOrder ? formatMoneyShort(weightedOrder) : EMPTY_CELL_VALUE;

export const formatCommitmentAmountCellText = ({
  commitmentAmount,
}: DealTrackingLimitedPartnerBreakdown): string =>
  commitmentAmount ? formatMoney(commitmentAmount) : EMPTY_CELL_VALUE;

export const formatAllocationAmountCellText = (
  { allocationAmount, isRejected }: DealTrackingLimitedPartnerBreakdown,
  { forExport = false }: { forExport?: boolean } = {},
): ReactNode[] => {
  const amountPart = allocationAmount
    ? formatMoney(allocationAmount)
    : EMPTY_CELL_VALUE;

  if (isRejected) {
    return richTextFormat(
      m.coInvestments_tracking_allocationAmount_declined({
        allocationAmount: amountPart,
      }),
      {
        0: ({ children }) => (forExport ? children : <span>{children}</span>),
      },
    );
  }

  return [amountPart];
};
